import React, {useContext, useEffect} from 'react';
import DateFnsUtils from '@date-io/date-fns';
import  it from 'date-fns/locale/it'
//import MomentUtils from '@date-io/moment';
// import moment from 'moment';
import {ThemeProvider} from '@material-ui/styles';
import {createMuiTheme as createMuiCoreTheme} from '@material-ui/core/styles';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';

import AppContext from '../AppContext';
import AppLocale from 'shared/localization';
import {responsiveFontSizes} from '@material-ui/core';
import {useBreakPointDown} from '../Utils';
import {NavStyle, ThemeMode, ThemeStyle, ThemeStyleRadius} from 'shared/constants/AppEnums';
import {useUrlSearchParams} from 'use-url-search-params';
import AppContextPropsType from '../../../types/AppContextPropsType';

import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
// import { StaticRouter } from 'react-router-dom/server';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { LinkProps } from '@mui/material/Link';
import { MUIScrollbarProps } from 'modules/hp/common/Style';
import { FaSortAlphaUp, FaSortAlphaDown } from 'react-icons/fa';


const LinkBehaviour = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return (
    <RouterLink data-testid='custom-link' ref={ref} to={href} {...other} />
  );
});

const cardRadius = ThemeStyleRadius.STANDARD;
const defaultMui5Theme = {
  palette: {
    background: {
      default: '#ECF2FF',
      paper: '#FFFFFF',
    },
  },
  status: {
    danger: 'orange',
  },
  divider: 'rgba(224, 224, 224, 1)',
  typography: {
    // fontFamily: ['Poppins', 'sans-serif'].join(','),
    fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehaviour,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehaviour,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: '#d32f2f',
          '&$error': {
            color: '#d32f2f',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        asterisk: {
          color: '#d32f2f',
          '&$error': {
            color: '#d32f2f',
          },
        },
      },
    },
    MuiCardLg: {
      styleOverrides: {
        root: {
          // apply theme's border-radius instead of component's default
          borderRadius:
            cardRadius === ThemeStyleRadius.STANDARD
              ? ThemeStyleRadius.STANDARD
              : ThemeStyleRadius.MODERN + 20,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: cardRadius,
          boxShadow: '0px 10px 10px 4px rgba(0, 0, 0, 0.04)',
          '& .MuiCardContent-root:last-of-type': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMultiSectionDigitalClock-root': MUIScrollbarProps,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: ThemeStyleRadius.STANDARD,
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: (props: any) => {
          if (props.className.includes('iconDirectionAsc')) {
            return <FaSortAlphaUp {...props} />;
          } else if (props.className.includes('iconDirectionDesc')) {
            return <FaSortAlphaDown {...props} />;
          } else {
            return null;
          }
        },
      },
      styleOverrides: {
        root: {
          '& .MuiTableSortLabel-icon': { transform: 'rotate(0deg)' },
        },
      },
    },
  },
};

const CremaThemeProvider: React.FC<React.ReactNode> = (props) => {
  const {
    theme,
    locale,
    isRTL,
    updateThemeMode,
    changeNavStyle,
    updateThemeStyle,
    setRTL,
    updateTheme,
  } = useContext<AppContextPropsType>(AppContext);
  const {muiLocale} = AppLocale[locale.locale];
  const isBelowMd = useBreakPointDown('md');

  const initailValue: InitialType = {};
  const types: TypesType = {};
  const [params] = useUrlSearchParams(initailValue, types);

  useEffect(() => {
    const updateQuerySetting = () => {
      if (params.theme_mode) {
        updateThemeMode!(params.theme_mode as ThemeMode);
      }
    };
    updateQuerySetting();
  }, [params.theme_mode, updateThemeMode]);

  useEffect(() => {
    const updateQuerySetting = () => {
      if (params.is_rtl) {
        setRTL!(params.is_rtl as boolean);
      }
      if (params.is_rtl || isRTL) {
        document.body.setAttribute('dir', 'rtl');
      } else {
        document.body.setAttribute('dir', 'ltr');
      }
    };
    updateQuerySetting();
  }, [isRTL, params.is_rtl, setRTL]);

  useEffect(() => {
    const updateQuerySetting = () => {
      if (params.nav_style) {
        changeNavStyle!(params.nav_style as NavStyle);
      }
    };
    updateQuerySetting();
  }, [changeNavStyle, params.nav_style]);

  useEffect(() => {
    const updateQuerySetting = () => {
      if (params.theme_style) {
        if (params.theme_style === ThemeStyle.MODERN) {
          if (isBelowMd) {
            theme.overrides.MuiCard.root.borderRadius = 20;
            theme.overrides.MuiToggleButton.root.borderRadius = 20;
          } else {
            theme.overrides.MuiCard.root.borderRadius = 30;
            theme.overrides.MuiToggleButton.root.borderRadius = 30;
          }
          theme.overrides.MuiButton.root.borderRadius = 30;
          theme.overrides.MuiCardLg.root.borderRadius = 50;
        } else {
          theme.overrides.MuiCard.root.borderRadius = 4;
          theme.overrides.MuiToggleButton.root.borderRadius = 4;
          theme.overrides.MuiButton.root.borderRadius = 4;
          theme.overrides.MuiCardLg.root.borderRadius = 4;
        }
        updateTheme!(theme);
        if (
          params.theme_style === ThemeStyle.MODERN ||
          params.theme_style === ThemeStyle.STANDARD
        ) {
          updateThemeStyle!(params.theme_style as ThemeStyle);
        }
      }
    };
    updateQuerySetting();
  }, [params.theme_style, theme, isBelowMd, updateTheme, updateThemeStyle]);

  return (
      <ThemeProvider theme={responsiveFontSizes(createMuiCoreTheme(theme, muiLocale))}>
          <MuiThemeProvider theme={createTheme(defaultMui5Theme)}>
              <MuiPickersUtilsProvider utils={/*MomentUtils*/ DateFnsUtils} locale={it}>
                  {props.children}
              </MuiPickersUtilsProvider>
          </MuiThemeProvider>
      </ThemeProvider>
  );
};

export default React.memo(CremaThemeProvider);
